import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { environmental_policies, ExperienceInclusions, getEnvironmentalIds, getIncludesIds, getTranslatedName, includes_policies } from '@utriper/core';
import { Container, Label, Title } from '../../styles';
import { Field, useFormState } from 'react-final-form';
import { AlertDialog, CharacterCounter, Checklist, Textarea } from '@utriper/core-ui';
import { Button, Collapse, Grid2, IconButton } from '@mui/material';
import { Add, KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material';
import AddIncludesOptionForm from 'modules/Experience/ExperienceWizard/components/AddIncludesOptionForm';

const AddButton = ({ onClick = () => {}, translate = () => {} }) => {
    return (
        <Button sx={{ color: 'var(--color-utriper-blue)', textTransform: 'none' }} variant="text" startIcon={<Add />} onClick={onClick}>
            {translate('add_new_option')}
        </Button>
    );
};

const IncludesForm = ({ form }) => {
    const { i18n, t } = useTranslation();
    const translate = (key) => t(`experience_form.${key}`);
    const { values } = useFormState();
    const [openIncludes, setOpenIncludes] = useState(false);
    const [openNotIncludes, setOpenNotIncludes] = useState(false);
    const [openEnv, setOpenEnv] = useState(false);
    const [openAdd, setOpenAdd] = useState(false);
    const [typeForm, setTypeForm] = useState(null);

    const defaultIncludesOptions = useMemo(
        () =>
            getIncludesIds()
                .slice(0, 6)
                .map((id) => ({
                    id,
                    includesId: id,
                    label: getTranslatedName(includes_policies, id, i18n.language),
                    disabled: false,
                })),
        [i18n.language]
    );

    const defaultEnvOptions = useMemo(
        () =>
            getEnvironmentalIds()
                .slice(0, 4)
                .map((id) => ({
                    id,
                    label: getTranslatedName(environmental_policies, id, i18n.language),
                })),
        [i18n.language]
    );

    const includesOptions = useMemo(() => {
        const selectedNotIncludes = values?.notIncludes || [];
        const allOptions = [...defaultIncludesOptions, ...values?.customIncludes];
        return allOptions.map((option) => ({
            ...option,
            disabled: option?.includesId ? selectedNotIncludes.includes(option?.id) : option?.disabled,
        }));
    }, [defaultIncludesOptions, values?.customIncludes, values?.notIncludes]);

    const notIncludesOptions = useMemo(() => {
        const selectedIncludes = values?.includes || [];
        const allOptions = [...defaultIncludesOptions, ...values?.customNotIncludes];
        return allOptions.map((option) => ({
            ...option,
            disabled: option?.includesId ? selectedIncludes.includes(option?.id) : option?.disabled,
        }));
    }, [defaultIncludesOptions, values?.customNotIncludes, values?.includes]);

    const environmentOptions = useMemo(() => [...defaultEnvOptions, ...values?.customEnvPolicies], [defaultEnvOptions, values?.customEnvPolicies]);

    const handleOpenAddForm = (typeForm = null) => {
        setOpenAdd(true);
        setTypeForm(typeForm);
    };

    const handleCloseForm = () => {
        setOpenAdd(false);
        setTypeForm(null);
    };

    const handleAddOption = ({ newOption }) => {
        if (!newOption) return;
        const uniqueId = `custom-${new Date().getTime()}-${Math.random()}`;

        const customOption = {
            id: typeForm ? uniqueId : 5,
            includesId: null,
            label: newOption,
            disabled: false,
        };

        if (typeForm === ExperienceInclusions.IDS.included) {
            form?.change('customIncludes', [...values?.customIncludes, customOption]);
            return;
        }

        if (typeForm === ExperienceInclusions.IDS.notincluded) {
            form?.change('customNotIncludes', [...values?.customNotIncludes, customOption]);
            return;
        }

        form?.change('customEnvPolicies', [...values?.customEnvPolicies, customOption]);
    };

    return (
        <React.Fragment>
            <Title title={translate('includes_form.title')} description={translate('includes_form.description')} />

            <Grid2 container size={12} spacing={1}>
                <Grid2 container size={{ xs: 12, md: 6 }} direction="column" justifyContent="flex-start" alignItems="flex-start" gap="8px">
                    {/* Pax Message */}
                    <Container>
                        <Label>{translate('pax_message')}</Label>
                        <Field
                            name="paxMessage"
                            component={Textarea}
                            placeholder={translate('pax_message_placeholder')}
                            minRows={4}
                            slotProps={{
                                input: {
                                    inputProps: {
                                        maxLength: 500,
                                    },
                                },
                            }}
                        />
                        <CharacterCounter length={values?.paxMessage?.length ?? 0} maxLength={500} />
                    </Container>
                </Grid2>

                <Grid2 container size={{ xs: 12, md: 6 }} direction="column" justifyContent="flex-start" alignItems="flex-start" gap="8px">
                    {/* Includes */}
                    <Container>
                        <Grid2 container size={12} justifyContent="space-between" alignItems="center">
                            <Label>{translate('includes')}</Label>
                            <IconButton onClick={() => setOpenIncludes(!openIncludes)}>{openIncludes ? <KeyboardArrowUp /> : <KeyboardArrowDown />}</IconButton>
                        </Grid2>
                        <Collapse in={openIncludes} timeout="auto" unmountOnExit>
                            <Container>
                                <Field name="includes" component={Checklist} options={includesOptions} />
                                <AddButton onClick={() => handleOpenAddForm(ExperienceInclusions.IDS.included)} translate={translate} />
                            </Container>
                        </Collapse>
                    </Container>

                    {/* Not Includes */}
                    <Container>
                        <Grid2 container size={12} justifyContent="space-between" alignItems="center">
                            <Label>{translate('not_includes')}</Label>
                            <IconButton onClick={() => setOpenNotIncludes(!openNotIncludes)}>{openNotIncludes ? <KeyboardArrowUp /> : <KeyboardArrowDown />}</IconButton>
                        </Grid2>
                        <Collapse in={openNotIncludes} timeout="auto" unmountOnExit>
                            <Container>
                                <Field name="notIncludes" component={Checklist} options={notIncludesOptions} />
                                <AddButton onClick={() => handleOpenAddForm(ExperienceInclusions.IDS.notincluded)} translate={translate} />
                            </Container>
                        </Collapse>
                    </Container>

                    {/* Environmental Policies */}
                    <Container>
                        <Grid2 container size={12} justifyContent="space-between" alignItems="center">
                            <Label>{translate('env_policies')}</Label>
                            <IconButton onClick={() => setOpenEnv(!openEnv)}>{openEnv ? <KeyboardArrowUp /> : <KeyboardArrowDown />}</IconButton>
                        </Grid2>
                        <Collapse in={openEnv} timeout="auto" unmountOnExit>
                            <Container>
                                <Field name="envPolicies" component={Checklist} options={environmentOptions} />
                                {environmentOptions?.length < 5 && <AddButton onClick={() => handleOpenAddForm(null)} translate={translate} />}
                            </Container>
                        </Collapse>
                    </Container>
                </Grid2>
            </Grid2>

            {/* Add Dialog */}
            <AlertDialog open={openAdd} handleClose={handleCloseForm} hideTitle hideActions maxWidth="sm" fullWidth dialogContent={<AddIncludesOptionForm onAddOption={handleAddOption} onClose={handleCloseForm} maxLength={typeForm ? 128 : 255} placeholder={typeForm ? translate('includes_placeholder') : translate('env_policies_placeholder')} />} />
        </React.Fragment>
    );
};

export default IncludesForm;
